/**
 * Timezone utility functions for consistent date handling across the application.
 *
 * The backend stores all times in UTC. The frontend must:
 * 1. Convert local times to UTC when SENDING to API
 * 2. Parse UTC strings (automatically converted to local by JavaScript Date)
 * 3. Display dates in the user's local timezone
 */

/**
 * Get the user's current timezone from the browser.
 * @returns IANA timezone string (e.g., "Asia/Qatar", "Asia/Kolkata")
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Convert a local Date to a UTC ISO string for API submission.
 *
 * This is the CORRECT way to send dates to the backend:
 * - Takes a Date object representing LOCAL time
 * - Returns a UTC ISO string that the backend will store
 *
 * @param localDate - Date object representing local time (e.g., user selected 5:00 PM local)
 * @returns UTC ISO string with milliseconds (e.g., "2026-01-22T11:30:00.000Z" if local was 5:00 PM IST)
 */
export function toUTCString(localDate: Date): string {
  return localDate.toISOString();
}

/**
 * Parse a UTC string from the API into a Date object.
 * JavaScript Date constructor automatically handles UTC to local conversion.
 *
 * @param utcString - UTC ISO string from the backend
 * @returns Date object in local timezone
 */
export function fromUTCString(utcString: string): Date {
  return new Date(utcString);
}

/**
 * Format a date for display in the user's local timezone.
 * @param date - Date object or ISO string
 * @returns Formatted date string (e.g., "22/01/2026")
 */
export function formatLocalDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Format a date and time for display in the user's local timezone.
 * @param date - Date object or ISO string
 * @returns Formatted datetime string (e.g., "22/01/2026 17:00")
 */
export function formatLocalDateTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * Format time only for display in the user's local timezone.
 * @param date - Date object or ISO string
 * @returns Formatted time string (e.g., "5:00 PM")
 */
export function formatLocalTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}
